import { ApplicationFormSettings } from './race-registration-form-settings';
import { RegistrationDisplayFlags } from './registration-display-flags';
import { VisibilityTypes } from './visibility-types';
import { CompetitorViewContext } from './competitor-view-context';

/**
 * Shared logic for resolving which application-form fields are "used" (should be rendered at all
 * for the current context) and "visible" (should be shown to the current viewer, e.g. public vs
 * admin), based on the event's ApplicationFormSettings display-flag / visibility values.
 *
 * `CompetitorViewHelper` (read-only table columns) and `CompetitorFormHelper` (editable
 * registration form) both extend this so the flag-resolution rules live in exactly one place.
 */
export abstract class ApplicationFormFieldVisibilityResolver<TFieldKey extends string> {
    public readonly allowedVisibilityTypes: VisibilityTypes[];
    public readonly allowedRegistrationDisplayFlags: RegistrationDisplayFlags[];

    protected constructor(protected readonly context: CompetitorViewContext) {
        this.allowedVisibilityTypes = this.resolveVisibilityTypes(context);
        this.allowedRegistrationDisplayFlags = this.resolveDisplayFlags(context);
    }

    /** Maps a field key to the ApplicationFormSettings property holding its VisibilityTypes value. */
    protected abstract get visibilityFieldMap(): Map<TFieldKey, keyof ApplicationFormSettings>;

    /** Maps a field key to the ApplicationFormSettings property holding its RegistrationDisplayFlags value. */
    protected abstract get displayFlagFieldMap(): Map<TFieldKey, keyof ApplicationFormSettings>;

    isFieldUsed(fieldName: TFieldKey, applicationFormSettings: ApplicationFormSettings | undefined): boolean {
        if (!applicationFormSettings) return false;

        const displayFlagKey = this.displayFlagFieldMap.get(fieldName);
        if (!displayFlagKey) return false;

        const fieldUsage = applicationFormSettings[displayFlagKey] as unknown;
        return typeof fieldUsage === 'number'
            ? this.allowedRegistrationDisplayFlags.includes(fieldUsage as RegistrationDisplayFlags)
            : false;
    }

    isFieldVisible(fieldName: TFieldKey, applicationFormSettings: ApplicationFormSettings | undefined): boolean {
        if (!applicationFormSettings) return false;

        const visibilityKey = this.visibilityFieldMap.get(fieldName);
        if (!visibilityKey) return false;

        const fieldVisibility = applicationFormSettings[visibilityKey] as unknown;
        return typeof fieldVisibility === 'number'
            ? this.allowedVisibilityTypes.includes(fieldVisibility as VisibilityTypes)
            : false;
    }

    shouldShowField(fieldName: TFieldKey, applicationFormSettings: ApplicationFormSettings | undefined): boolean {
        return this.isFieldUsed(fieldName, applicationFormSettings) && this.isFieldVisible(fieldName, applicationFormSettings);
    }

    isAdmin(): boolean {
        return this.context === CompetitorViewContext.ADMINISTRATION;
    }

    isSelf(): boolean {
        return this.context === CompetitorViewContext.COMPETITOR_SELF;
    }

    isCustomFieldVisible(customField: { visibility?: VisibilityTypes; registrationDisplayFlag?: RegistrationDisplayFlags }): boolean {
        return (
            !!customField.visibility &&
            this.allowedVisibilityTypes.includes(customField.visibility) &&
            !!customField.registrationDisplayFlag &&
            this.allowedRegistrationDisplayFlags.includes(customField.registrationDisplayFlag)
        );
    }

    private resolveVisibilityTypes(context: CompetitorViewContext): VisibilityTypes[] {
        switch (context) {
            case CompetitorViewContext.PUBLIC:
            case CompetitorViewContext.LEAGUE_ORGANISER:
                return [VisibilityTypes.ALWAYS_VISIBLE, VisibilityTypes.VISIBLE_TO_PUBLIC];
            case CompetitorViewContext.COMPETITOR_SELF:
            case CompetitorViewContext.ADMINISTRATION:
                return [
                    VisibilityTypes.ALWAYS_VISIBLE,
                    VisibilityTypes.VISIBLE_TO_PUBLIC,
                    VisibilityTypes.VISIBLE_TO_ADMINISTRATION,
                ];
            default:
                return [VisibilityTypes.ALWAYS_VISIBLE];
        }
    }

    private resolveDisplayFlags(context: CompetitorViewContext): RegistrationDisplayFlags[] {
        switch (context) {
            case CompetitorViewContext.PUBLIC:
            case CompetitorViewContext.LEAGUE_ORGANISER:
            case CompetitorViewContext.COMPETITOR_SELF:
                return [RegistrationDisplayFlags.ALWAYS_USED, RegistrationDisplayFlags.USED_FOR_PUBLIC_REGISTRATION];

            case CompetitorViewContext.ADMINISTRATION:
                return [
                    RegistrationDisplayFlags.ALWAYS_USED,
                    RegistrationDisplayFlags.USED_FOR_PUBLIC_REGISTRATION,
                    RegistrationDisplayFlags.USED_FOR_ADMINISTRATIVE_REGISTRATION,
                ];
            default:
                return [RegistrationDisplayFlags.ALWAYS_USED];
        }
    }
}